/**
 * Text for the DueBadge (kanban 3d77f755), kept out of the component file so
 * the page keeps React fast refresh and the wording is unit-testable alone.
 */
import type { DueUrgency, OrderRecord } from '../../types/orders';
import { formatIsoDate } from './ParkedBadge';

export type DueOrder = Pick<
  OrderRecord,
  | 'id'
  | 'due_urgency'
  | 'due_date'
  | 'due_business_days'
  | 'is_will_call'
  | 'due_desk_state'
  | 'due_desk_by'
  | 'due_desk_until'
>;

export type DueTone = DueUrgency | 'handled';

/** Which of the four looks a row gets: the desk's answer mutes every stage. */
export function dueTone(order: Pick<DueOrder, 'due_urgency' | 'due_desk_state'>): DueTone | null {
  if (!order.due_urgency) return null;
  return order.due_desk_state ? 'handled' : order.due_urgency;
}

/** "Emily" from "Emily Adams" / "emily@chemmasters.net" / "ER". */
export function deskFirstName(by: string | null | undefined): string {
  const raw = (by || '').split('@')[0].trim();
  if (!raw) return 'The desk';
  const first = raw.split(/[\s._]+/)[0];
  return first.length <= 3 ? first.toUpperCase() : first[0].toUpperCase() + first.slice(1);
}

/** " · Emily has this" / " · saved for later until 10/2" — empty when the alert is still open. */
export function dueDeskSuffix(order: DueOrder): string {
  if (order.due_desk_state === 'acknowledged') return ` · ${deskFirstName(order.due_desk_by)} has this`;
  if (order.due_desk_state === 'saved_for_later') {
    const until = order.due_desk_until ? ` until ${formatIsoDate(order.due_desk_until)}` : '';
    return ` · saved for later${until}`;
  }
  return '';
}

/** "OVERDUE 8/7" / "DUE TODAY" / "DUE 8/11", plus " · PICKUP" for will-call. */
export function dueBadgeLabel(order: DueOrder): string | null {
  if (!order.due_urgency) return null;
  const when = order.due_date ? formatIsoDate(order.due_date) : '';
  const lead =
    order.due_urgency === 'overdue' ? `OVERDUE${when ? ` ${when}` : ''}` :
    order.due_urgency === 'due_today' ? 'DUE TODAY' :
    `DUE${when ? ` ${when}` : ''}`;
  return `${order.is_will_call ? `${lead} · PICKUP` : lead}${dueDeskSuffix(order)}`;
}

export function dueBadgeTitle(order: DueOrder): string {
  const what = order.is_will_call ? 'pickup' : 'ship';
  const n = order.due_business_days ?? 0;
  const days = `${Math.abs(n)} business day${Math.abs(n) === 1 ? '' : 's'}`;
  let base: string;
  if (order.due_urgency === 'overdue') {
    base = `Requested ${what} date ${order.due_date ?? ''} passed ${days} ago and this order is still a draft`;
  } else if (order.due_urgency === 'due_today') {
    base = `Requested ${what} date is today and this order is still a draft`;
  } else {
    base = `Requested ${what} date ${order.due_date ?? ''} is the next business day and this order is still a draft`;
  }
  if (order.due_desk_state === 'acknowledged') {
    return `${base}. ${deskFirstName(order.due_desk_by)} acknowledged the alert: the desk has it.`;
  }
  if (order.due_desk_state === 'saved_for_later') {
    const until = order.due_desk_until ? ` until ${formatIsoDate(order.due_desk_until)}` : '';
    return `${base}. ${deskFirstName(order.due_desk_by)} saved the alert for later${until}.`;
  }
  return base;
}

